/**
 * BS Search Functions
 * Bootstrap 5 Autocomplete for OXID frontend search
 *
 * Vanilla JS implementation (OXID 7.4+ ready)
 *
 * @author  Mario Lorenz, www.buchsuite.de
 * @license https://www.gnu.org/licenses/gpl-2.0.html GNU General Public License, version 2 (one or other)
 */

/**
 * Vanilla JS Autocomplete for Bootstrap 5.
 * Provides dropdown suggestions for search input fields.
 */
export class Bootstrap5Autocomplete {
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            threshold: 4,
            maximumItems: 5,
            highlightTyped: true,
            highlightClass: 'text-primary',
            // true: the source is the server's answer - already filtered (every word, any order)
            // and already highlighted with sBSAutosuggestHighlightMarkup, so neither is redone here
            serverFiltered: false,
            dropdownClass: '',
            source: {},
            onSelectItem: null,
            ...options
        };

        this.dropdown = null;
        this.dropdownMenu = null;
        this.isInitialized = false;
        // Kept so the listeners can be detached again in unbindEvents().
        this.handlers = null;

        this.init();
    }

    init() {
        this.cleanup();
        this.setupDropdown();
        this.bindEvents();
        this.isInitialized = true;
    }

    cleanup() {
        // Detach the listeners of the previous instance first. Without this every rebuild (one
        // per keystroke batch) left its click handler attached to the same input; the handler of
        // the very first instance still had an empty source, so it hit the createItems() === 0
        // branch and called stopPropagation() - the click that should open the dropdown never
        // arrived and the suggestions stayed hidden.
        this.unbindEvents();

        // Remove existing dropdown
        const existingDropdown = this.element.parentNode.querySelector('.dropdown-menu');
        if (existingDropdown) {
            existingDropdown.remove();
        }

        // Remove dropdown classes
        this.element.parentNode.classList.remove('dropdown');
        this.element.removeAttribute('data-bs-toggle');
        this.element.classList.remove('dropdown-toggle');

        // Dispose existing Bootstrap dropdown
        if (this.dropdown) {
            this.dropdown.dispose();
        }
    }

    setupDropdown() {
        // Setup dropdown structure
        this.element.parentNode.classList.add('dropdown');
        this.element.setAttribute('data-bs-toggle', 'dropdown');
        this.element.classList.add('dropdown-toggle');

        // Create dropdown menu
        this.dropdownMenu = document.createElement('div');
        this.dropdownMenu.className = 'dropdown-menu';
        if (this.options.dropdownClass) {
            this.dropdownMenu.classList.add(this.options.dropdownClass);
        }

        this.element.parentNode.appendChild(this.dropdownMenu);

        // Initialize Bootstrap dropdown
        this.dropdown = new bootstrap.Dropdown(this.element);
    }

    bindEvents() {
        // Referenced instead of inlined, so unbindEvents() can remove exactly these listeners
        // again - an anonymous arrow function cannot be detached.
        this.handlers = {
            click: (e) => {
                if (this.createItems() === 0) {
                    e.stopPropagation();
                    this.dropdown.hide();
                }
            },
            keyup: () => {
                if (this.createItems() > 0) {
                    this.dropdown.show();
                } else {
                    this.element.click();
                }
            }
        };

        this.element.addEventListener('click', this.handlers.click);
        this.element.addEventListener('keyup', this.handlers.keyup);
    }

    /**
     * Detaches the listeners registered by bindEvents().
     *
     * @return {void}
     */
    unbindEvents() {
        if (!this.handlers) {
            return;
        }

        this.element.removeEventListener('click', this.handlers.click);
        this.element.removeEventListener('keyup', this.handlers.keyup);
        this.handlers = null;
    }

    /**
     * Sets the transient dropdown state (loading / no results) that the stylesheet renders via
     * `content: attr(data-loading-text)` / `attr(data-no-results-text)`.
     *
     * Uses classList rather than assigning className: a full assignment also dropped the `show`
     * class Bootstrap puts on the menu, so the list was populated but invisible.
     *
     * @param {string|null} state 'loading', 'no-results', or null to clear both
     * @param {string}      text  translated message to display
     *
     * @return {void}
     */
    setMenuState(state, text) {
        if (!this.dropdownMenu) {
            return;
        }

        this.dropdownMenu.classList.remove('loading', 'no-results');
        this.dropdownMenu.removeAttribute('data-loading-text');
        this.dropdownMenu.removeAttribute('data-no-results-text');

        if (!state) {
            return;
        }

        this.dropdownMenu.classList.add(state);
        this.dropdownMenu.setAttribute(
            state === 'loading' ? 'data-loading-text' : 'data-no-results-text',
            text || ''
        );
    }

    createItems() {
        const lookup = this.element.value;
        if (lookup.length < this.options.threshold) {
            this.dropdown.hide();
            return 0;
        }

        this.dropdownMenu.innerHTML = '';
        let count = 0;

        const keys = Object.keys(this.options.source);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const object = this.options.source[key];
            const item = {
                label: object.label || key,
                html: object.html || '',
                value: object.value || object
            };

            if (this.options.serverFiltered || item.label.toLowerCase().indexOf(lookup.toLowerCase()) >= 0) {
                this.dropdownMenu.appendChild(this.createItem(lookup, item));
                if (this.options.maximumItems > 0 && ++count >= this.options.maximumItems) {
                    break;
                }
            }
        }

        // Bind click events to dropdown items
        this.dropdownMenu.querySelectorAll('.dropdown-item').forEach(item => {
            item.addEventListener('click', () => {
                this.element.value = item.textContent;
                if (this.options.onSelectItem) {
                    this.options.onSelectItem({
                        value: item.dataset.value,
                        label: item.textContent
                    }, this.element);
                }
            });
        });

        return this.dropdownMenu.children.length;
    }

    createItem(lookup, item) {
        let label;
        if (this.options.serverFiltered && item.html) {
            // the server's markup: highlighted (or not) as the module settings say. Safe as HTML -
            // the shop index holds normalised text only and platform texts are escaped server-side
            label = item.html;
        } else if (this.options.highlightTyped) {
            const idx = item.label.toLowerCase().indexOf(lookup.toLowerCase());
            label = item.label.substring(0, idx)
                + '<span class="' + this.expandClassArray(this.options.highlightClass) + '">'
                + item.label.substring(idx, idx + lookup.length) + '</span>'
                + item.label.substring(idx + lookup.length, item.label.length);
        } else {
            label = item.label;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dropdown-item';
        button.dataset.value = item.value;
        button.innerHTML = label;

        return button;
    }

    expandClassArray(classes) {
        if (typeof classes === "string") {
            return classes;
        }
        if (classes.length === 0) {
            return '';
        }
        return classes.join(' ');
    }

    updateSource(newSource) {
        this.options.source = newSource;
    }

    dispose() {
        this.cleanup();
        this.isInitialized = false;
    }
}

/**
 * Initialize the search autocomplete functionality.
 * Called automatically on DOMContentLoaded.
 *
 * @return {void}
 */
export function init() {
    // Check if Bootstrap 5 is loaded
    if (typeof bootstrap === 'undefined') {
        console.error('Bootstrap 5 is not loaded!');
        return;
    }

    if (typeof sBaseUrl === 'undefined') {
        console.error('sBaseUrl is not defined! Using fallback...');
        window.sBaseUrl = window.location.origin + window.location.pathname + '?';
    }

    const searchInput = document.getElementById('searchParam');
    if (!searchInput) {
        return;
    }

    let autocompleteInstance = null;
    let searchTimeout;

    // The texts come from data attributes on the input, filled by the module template from
    // BS_SEARCH_LOADING / BS_SEARCH_NO_RESULTS. Read once here instead of falling back to English
    // literals at each call site: a missing ident then shows no message rather than an
    // untranslated one.
    const i18n = {
        loading: searchInput.dataset.loadingText || '',
        noResults: searchInput.dataset.noResultsText || ''
    };

    // iBSAutosuggestMaxResults from the template - the server already limits its answer to it,
    // a fixed cap here used to cut every larger setting down to 10
    const maximumItems = parseInt(searchInput.dataset.maxResults || '0', 10) || 0;

    // Function to handle item selection
    function onSelectItem(item, element) {
        // Set the input value to the selected item
        element.value = item.label;

        // Submit the form
        const form = document.getElementById('searchForm');
        if (form) {
            form.submit();
        }
    }

    // Dynamic source function for real API data
    function getDynamicSource(query) {
        const url = (sBaseUrl + 'cl=AutoSuggestController&fnc=getResult&search=' + encodeURIComponent(query)).replace(/&amp;/g, '&');

        return fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                // Process the data into the format expected by autocomplete
                const processedData = {};

                if (Array.isArray(data)) {
                    data.forEach(function(item, index) {
                        const label = item.name || item.label || item.value || '';
                        const value = item.value || item.name || item.label || '';

                        // plain text for the input field and the de-duplication, the server's
                        // (highlighted) markup for the display
                        const cleanLabel = label.replace(/(<([^>]+)>)/ig, '');

                        if (cleanLabel.trim() !== '') {
                            processedData[cleanLabel] = {
                                value: value.replace(/(<([^>]+)>)/ig, ''),
                                label: cleanLabel,
                                html: label
                            };
                        }
                    });
                }

                return processedData;
            });
    }

    // Function to update autocomplete source
    function updateAutocompleteSource(query) {
        // Clear previous timeout
        clearTimeout(searchTimeout);

        // Show loading state
        if (autocompleteInstance) {
            autocompleteInstance.setMenuState('loading', i18n.loading);
        }

        // Set timeout for debouncing
        searchTimeout = setTimeout(function() {
            getDynamicSource(query)
                .then(function(data) {
                    // Check if we have results
                    const hasResults = Object.keys(data).length > 0;

                    if (!hasResults) {
                        // Show no results state
                        if (autocompleteInstance) {
                            autocompleteInstance.setMenuState('no-results', i18n.noResults);
                        }
                        return;
                    }

                    // Update the source and reinitialize
                    if (autocompleteInstance) {
                        autocompleteInstance.dispose();
                    }

                    autocompleteInstance = new Bootstrap5Autocomplete(searchInput, {
                        source: data,
                        onSelectItem: onSelectItem,
                        highlightClass: 'text-primary',
                        threshold: 1,
                        maximumItems: maximumItems,
                        highlightTyped: false,
                        serverFiltered: true,
                        dropdownClass: 'search-dropdown'
                    });

                    // Manually trigger the dropdown
                    searchInput.click();
                })
                .catch(function(error) {
                    // Show error state or no results
                    if (autocompleteInstance) {
                        autocompleteInstance.setMenuState('no-results', i18n.noResults);
                    }
                    console.warn('Autocomplete error:', error);
                });
        }, 300); // 300ms debounce
    }

    // Initialize with empty source first
    autocompleteInstance = new Bootstrap5Autocomplete(searchInput, {
        source: {},
        onSelectItem: onSelectItem,
        highlightClass: 'text-primary',
        threshold: 1,
        maximumItems: maximumItems,
        highlightTyped: false,
        serverFiltered: true,
        dropdownClass: 'search-dropdown'
    });

    // Override the keyup event to load dynamic data
    searchInput.addEventListener('keyup', function() {
        const query = this.value.trim();

        if (query.length >= 1) {
            updateAutocompleteSource(query);
        }
    });

    // Handle Enter key and search button
    searchInput.addEventListener('keypress', function(e) {
        if (e.which === 13 || e.keyCode === 13) {
            e.preventDefault();
            const value = this.value.trim();
            if (value !== '') {
                const form = document.getElementById('searchForm');
                if (form) {
                    form.submit();
                }
            }
        }
    });

    // Handle search button click
    const searchSubmit = document.getElementById('searchSubmit');
    if (searchSubmit) {
        searchSubmit.addEventListener('click', function(e) {
            e.preventDefault();
            const value = searchInput.value.trim();
            if (value !== '') {
                const form = document.getElementById('searchForm');
                if (form) {
                    form.submit();
                }
            }
        });
    }
}

// Auto-initialize on DOMContentLoaded
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // DOM already loaded
        init();
    }
}
